import { useCallback, useMemo } from 'react';

import { useProductContext } from '@partful/shared/lib/providers/useProductContext';
import { useTenant } from '@providers/Tenant';
import { useUser } from '@providers/User';

export interface UseGoogleAnalyticsReturn {
  /**
   * Function to send an event to Google Analytics.
   * @param eventName - Name of the event to send.
   */
  sendEvent: (eventName: string) => void;
}

interface GoogleAnalyticsEventParameters {
  user: string;
  account: string;
  role: string;
}

/**
 * Custom React hook to send events to Google Analytics
 * Must be used within a UserProvider component.
 * Must be used within a TenantProvider component.
 * Should be used within a ProductContextProvider component.
 *
 * @returns An object containing:
 *   - `sendEvent`: A function that sends an event to Google Analytics.
 *
 * @example
 * const { sendEvent } = useGoogleAnalytics();
 * sendEvent(eventName);
 */
export const useGoogleAnalytics = (): UseGoogleAnalyticsReturn => {
  const { username, role } = useUser();
  const { accountId } = useTenant();
  const { name } = useProductContext();

  const eventParameters: GoogleAnalyticsEventParameters = useMemo(
    () => ({
      user: username!,
      account: accountId,
      role: role!,
      product: name,
    }),
    [username, role, accountId, name]
  );

  const sendEvent = useCallback(
    (eventName: string) => {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, eventParameters);
      }
    },
    [eventParameters]
  );

  return {
    sendEvent,
  };
};
